import { memo, useCallback, useState } from 'react';
import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useBoolean,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import AddButtonV1 from 'components/AddButtonV1';
import CheckIcon from 'components/Icons/CheckIcon';
import PlanPermission from 'container/PlanPermission';
import { Scenario } from 'models/response/scenario.interface';
import ScenarioPermission from 'pages/PermissionComponent/ScenarioPermission';
import { getAllScenarios, getBaseScenario, selectBaseScenario } from 'redux/PlannerModeSlice';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import { BUILDER_MODE_V1_LAYOUT, BUILDER_MODE_V1_TOKENS } from '../constants';
import BuilderModeSvgIcon from '../icons/BuilderModeSvgIcon';

import AddScenarioModalV1 from './AddScenarioModalV1';
import OutlineActionButton from './OutlineActionButton';
import scenarioPopoverContentProperties from './scenarioPopoverStyles';

interface BaselineScenarioDropdownProperties {
  planPermission?: boolean;
}

const BaselineScenarioDropdown = memo(
  ({ planPermission = false }: BaselineScenarioDropdownProperties) => {
    const dispatch = useDispatch<AppDispatch>();
    const scenarios = useSelector(getAllScenarios);
    const baseScenario = useSelector(getBaseScenario);
    const userSubscriptionLimits = useSelector(getUserSubscriptionLimits);
    const [isOpen, setIsOpen] = useBoolean();
    const [isAddScenarioModalOpen, setIsAddScenarioModalOpen] = useBoolean();
    const [isPermissionComponent, setIsPermissionComponent] = useState(false);

    const selectScenario = useCallback(
      (scenario: Scenario) => {
        dispatch(selectBaseScenario(scenario));
        setIsOpen.off();
      },
      [dispatch, setIsOpen],
    );

    const handleAddScenarioClick = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();

        if (planPermission) {
          return;
        }

        const scenarioLimit =
          userSubscriptionLimits?.subscription_features_limit?.scenario_per_model;
        const scenariosLength = scenarios?.length ?? 0;

        if (
          scenarioLimit !== null &&
          (scenariosLength === scenarioLimit || scenariosLength >= scenarioLimit)
        ) {
          setIsPermissionComponent(true);
          return;
        }

        setIsPermissionComponent(false);
        setIsOpen.on();
        setIsAddScenarioModalOpen.on();
      },
      [
        planPermission,
        scenarios?.length,
        setIsAddScenarioModalOpen,
        setIsOpen,
        userSubscriptionLimits?.subscription_features_limit?.scenario_per_model,
      ],
    );

    const triggerButton = (
      <OutlineActionButton
        label={baseScenario?.name ?? 'Baseline'}
        leftIcon={<BuilderModeSvgIcon name='branch' />}
        minW={BUILDER_MODE_V1_LAYOUT.baselineButtonWidth}
        w={BUILDER_MODE_V1_LAYOUT.baselineButtonWidth}
        aria-label='Select baseline scenario'
      />
    );

    if (!scenarios?.length) {
      return triggerButton;
    }

    return (
      <>
        <Popover
          placement='bottom-start'
          isOpen={isOpen}
          onOpen={setIsOpen.on}
          onClose={setIsOpen.off}
          isLazy
          lazyBehavior='keepMounted'
          gutter={4}
          closeOnBlur={!isAddScenarioModalOpen}
        >
          <PopoverTrigger>
            <Box as='span' display='inline-block' w={BUILDER_MODE_V1_LAYOUT.baselineButtonWidth}>
              {triggerButton}
            </Box>
          </PopoverTrigger>
          <PopoverContent sx={scenarioPopoverContentProperties}>
            <PopoverBody p={0}>
              <Flex
                direction='column'
                gap='1px'
                bg={BUILDER_MODE_V1_TOKENS.borderSubtle}
                borderRadius={BUILDER_MODE_V1_LAYOUT.scenarioPopoverRadius}
                overflow='hidden'
              >
                <Box bg={BUILDER_MODE_V1_TOKENS.white} pt={4} pb={2} px={4}>
                  <Text
                    fontSize='16px'
                    fontWeight='500'
                    color={BUILDER_MODE_V1_TOKENS.textDark}
                    lineHeight='normal'
                    fontFamily={BUILDER_MODE_V1_LAYOUT.toolbarFontFamily}
                  >
                    Select Scenario
                  </Text>
                </Box>

                <Flex direction='column' gap='1px' maxH='240px' overflowY='auto' className='scroll'>
                  {scenarios.map((scenario: Scenario) => {
                    const isSelected = baseScenario?.id === scenario.id;
                    return (
                      <Flex
                        key={scenario.id}
                        align='center'
                        justify='space-between'
                        gap='10px'
                        px={4}
                        py={3}
                        bg={BUILDER_MODE_V1_TOKENS.white}
                        cursor='pointer'
                        _hover={{ bg: BUILDER_MODE_V1_TOKENS.activeBg }}
                        onClick={() => selectScenario(scenario)}
                      >
                        <Text
                          flex='1'
                          minW={0}
                          fontSize='14px'
                          color={BUILDER_MODE_V1_TOKENS.textDark}
                          fontWeight='400'
                          lineHeight='normal'
                          fontFamily={BUILDER_MODE_V1_LAYOUT.toolbarFontFamily}
                          noOfLines={1}
                        >
                          {scenario.name}
                        </Text>
                        <Box flexShrink={0} w='18px' h='18px'>
                          {isSelected ? (
                            <CheckIcon />
                          ) : (
                            <Box
                              w='18px'
                              h='18px'
                              borderRadius='full'
                              border='1px solid'
                              borderColor={BUILDER_MODE_V1_LAYOUT.inputPanelHeaderMid}
                              bg={BUILDER_MODE_V1_TOKENS.white}
                            />
                          )}
                        </Box>
                      </Flex>
                    );
                  })}
                </Flex>

                <Box
                  as='button'
                  type='button'
                  display='flex'
                  alignItems='center'
                  gap={2}
                  px={4}
                  py={3}
                  bg={BUILDER_MODE_V1_TOKENS.white}
                  cursor={planPermission ? 'not-allowed' : 'pointer'}
                  w='100%'
                  textAlign='left'
                  _hover={{ bg: BUILDER_MODE_V1_TOKENS.activeBg }}
                  onClick={handleAddScenarioClick}
                >
                  <AddButtonV1 />
                  <Text
                    fontSize='14px'
                    fontWeight='500'
                    color={BUILDER_MODE_V1_TOKENS.primary}
                    lineHeight='normal'
                    fontFamily={BUILDER_MODE_V1_LAYOUT.toolbarFontFamily}
                    whiteSpace='nowrap'
                  >
                    Add New Scenario
                  </Text>
                </Box>
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Popover>

        <AddScenarioModalV1
          isOpen={isAddScenarioModalOpen}
          onClose={setIsAddScenarioModalOpen.off}
        />

        {isPermissionComponent && (
          <ScenarioPermission
            ScenarioLimit={userSubscriptionLimits?.subscription_features_limit?.scenario_per_model}
            CurrentScenarioLength={scenarios?.length}
          />
        )}
      </>
    );
  },
);

BaselineScenarioDropdown.displayName = 'BaselineScenarioDropdown';

export default PlanPermission(BaselineScenarioDropdown);
